import { quantitative, section, number, equation, table, example } from '../batch2/shared.ts';
export const batch3Math = [
  quantitative(
    'math-matching-cards',
    'math',
    'Three cards',
    [
      section('samples', 'Count equally likely selections', [
        'Probability compares favorable outcomes with all possible outcomes only when the outcomes being counted are equally likely. For cards drawn uniformly without replacement, every ordered list of distinct cards is equally likely. If the question ignores draw order, every three-card selection corresponds to the same six possible orders, so unordered selections are equally likely too.',
        'A combination C(n,k) counts ways to choose k objects from n without order. Choosing them in order gives n × (n − 1) × … × (n − k + 1). Each selection was counted k! times, once for every ordering of its k objects, so divide by that number. The objects here are individual cards: two cards bearing the same letter remain different physical outcomes.',
        equation(
          'Unordered selections',
          'C(n,k)',
          'n(n − 1)…(n − k + 1) / k!',
          'For k = 3 the denominator is 3 × 2 × 1 = 6.',
        ),
        example(
          'Two cards from three pairs',
          'Suppose the cards are A₁, A₂, B₁, B₂, C₁ and C₂ and two are drawn. There are C(6,2) = 15 selections. Exactly three selections match, one for each letter. Thus the chance of a pair is 3/15 = 1/5.',
          table(
            'Count by structure',
            ['Selection type', 'Count'],
            [
              ['Matching letters', '3'],
              ['Two different letters', 'C(3,2) × 2 × 2 = 12'],
              ['All selections', '15'],
            ],
          ),
          'The factor two for each chosen letter reflects which physical copy is drawn. Forgetting these factors treats letter patterns as equally likely even though they represent different numbers of card outcomes.',
        ),
        'A complement is the set of outcomes where the requested event does not occur. An event and its complement partition the full sample space, so their probabilities add to one. Counting the complement is useful when “no match” has a simpler structure than “at least one match.” The two counts must still refer to the same kind of outcome.',
      ]),
      section(
        'apply',
        'Count three different letters',
        [
          'There are eight physical cards and C(8,3) = 56 equally likely three-card selections. To avoid a pair, choose three of the four letters in C(4,3) = 4 ways, then choose one of the two cards for each letter in 2³ = 8 ways. This gives 32 no-pair selections.',
          equation(
            'Use the complement',
            'P(at least one pair)',
            '1 − 32/56 = 3/7',
            'The remaining 24 selections each contain one complete pair.',
          ),
          'As a second check, choose the paired letter in four ways. The third card can be any of the six cards of another letter, giving 24 selections directly. No selection is double-counted because three cards cannot contain two complete pairs. This shortcut would require revision for a four-card draw, where two pairs are possible. Independence cannot be assumed between the successive draws because every removed card changes the composition of the deck.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b3-cardpairs',
      'Eight cards are labeled A, A, B, B, C, C, D, D. Draw three individual cards uniformly without replacement. What is the probability that the draw contains both copies of at least one letter? Give a fraction or decimal.',
      3 / 7,
      'probability',
      'Of the 56 equally likely selections, 32 have three different letters; the remaining 24 contain a pair.',
      ['samples', 'apply'],
    ),
  ),
  quantitative(
    'math-dilution',
    'math',
    'Two replacements',
    [
      section('concentration', 'Track the amount that is conserved', [
        'Concentration is an amount of substance per amount of mixture. For a well-mixed tank, any portion removed has the same concentration as the tank immediately before removal. If c is the fraction of the mixture that is solute, removing v liters removes c × v liters of solute under a volume-additive model.',
        'The useful state variable is the amount of solute, not the concentration alone. Adding pure water changes the denominator of concentration but adds no solute. Removing a mixed portion reduces both solute and total liquid. Write down these two amounts separately before recombining them into a concentration.',
        equation(
          'Concentration as a fraction',
          'c',
          'amount of solute / total mixture volume',
          'A 30% mixture has c = 0.30, not 30.',
        ),
        'If a tank of fixed restored volume V is mixed and a volume v is replaced with pure water, the fraction of solute retained is 1 − v/V. After replacement the total volume is back to V, so the new concentration is the old concentration multiplied by that same factor. Repeating the operation multiplies again; it does not subtract the same absolute solute amount each time.',
        example(
          'A smaller mixture',
          'A 10-liter mixture contains 2 liters of solute. Remove 5 liters after mixing, then replace it with water. Half the solute remains, so the restored mixture contains 1 liter of solute. A second identical replacement leaves half of that, or 0.5 liter.',
          table(
            'A repeated replacement',
            ['Stage', 'Solute', 'Total volume', 'Concentration'],
            [
              ['Initially', '2 L', '10 L', '20%'],
              ['After replacement 1', '1 L', '10 L', '10%'],
              ['After replacement 2', '0.5 L', '10 L', '5%'],
            ],
          ),
          'The same liquid volume is removed each time, but the second removal contains less solute because the first replacement diluted the tank.',
        ),
      ]),
      section(
        'application',
        'Keep the changing concentration in the ledger',
        [
          'The daily tank initially contains 30 × 0.40 = 12 liters of solute. Each replacement removes one third of the well-mixed liquid, retaining two thirds of the solute. After two replacements, the retained amount is 12 × (2/3)² = 16/3 liters.',
          equation(
            'Final fraction',
            'c after two replacements',
            '(16/3) / 30 = 8/45',
            'The final volume is 30 liters because the removed liquid is replaced each time.',
          ),
          'The final concentration is approximately 0.17778, or 17.778%. Subtracting 4 liters of solute twice would incorrectly use the original concentration for the second removal. After the first step, only 8 liters remain, so the second step removes 8/3 liters. A useful limit check is that repeated partial replacements approach zero solute but never reach exactly zero in finitely many steps under this ideal mixing model.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b3-dilution',
      'A 30-liter tank contains a perfectly mixed solution that is 40% solute by volume. Remove 10 liters and replace it with 10 liters of pure water. Mix fully, then repeat the same remove-and-replace operation once more. Assume volumes add and no reaction or evaporation occurs. What fraction of the final mixture is solute? Give a fraction, decimal or percentage.',
      8 / 45,
      'concentration',
      'Each replacement retains two thirds of the solute; the final concentration is 0.40 × (2/3)² = 8/45.',
      ['concentration', 'application'],
    ),
  ),
  quantitative(
    'math-flight-height',
    'math',
    'Time above a platform',
    [
      section('functions', 'A height function describes many moments', [
        'A function assigns a height to each time. A formula such as h(t) = at² + bt + c is quadratic because its highest power of time is two. The coefficient c gives height at time zero. When a is negative, the height-versus-time graph bends downward: the object rises more slowly, reaches a highest point, and then falls in this idealized model.',
        'Asking when the object is at a specified height is an equation. Asking how long it is above that height is an inequality and an interval-length question. First find the boundary times by solving h(t) = H. Then decide which times between or outside those boundaries satisfy h(t) > H. Finally subtract the starting time from the ending time.',
        equation(
          'Measure height relative to the threshold',
          'height difference from the threshold',
          'h(t) − H',
          'Subtracting H makes the difference positive exactly when h(t) is greater than H. Zero means at the threshold; a negative difference means below it.',
        ),
        example(
          'A distinct quadratic',
          'Suppose h(t) = −t² + 6t meters and the level is 5 meters. The equality becomes −t² + 6t − 5 = 0, or −(t − 1)(t − 5) = 0. The boundary times are 1 and 5 seconds. Between them, one factor is positive and the other negative; the leading minus makes their product positive.',
          table(
            'Check the sign',
            ['Time', 'h(t) − 5', 'Position'],
            [
              ['0', '−5', 'Below'],
              ['3', '4', 'Above'],
              ['6', '−5', 'Below'],
            ],
          ),
          'The object is above the level for 5 − 1 = 4 seconds. The sum of the roots is not the elapsed duration. Excluding the two exact boundary instants does not change an interval’s length.',
        ),
        'Factoring works by reversing multiplication: if a product is zero, at least one factor is zero. It does not say that one factor must be zero when the product is merely positive. If convenient factoring is unavailable, the quadratic formula or completing the square can locate the equality boundaries before the same sign analysis.',
      ]),
      section(
        'apply',
        'Find and compare the two crossing times',
        [
          'The daily threshold equation is 20t − 5t² = 15. Rearranging gives −5(t² − 4t + 3) = 0. The inner quadratic factors as (t − 1)(t − 3), so the object crosses the 15-meter level at times 1 and 3 seconds.',
          equation(
            'Height relative to the platform',
            'h(t) − 15',
            '−5(t − 1)(t − 3)',
            'This is positive between the two roots.',
          ),
          'At t = 2 the height is 20 meters, confirming that the middle interval is above the platform. The duration is therefore 3 − 1 = 2 seconds. The stated domain 0 ≤ t ≤ 4 is the complete modeled flight and contains both crossings. In a different problem, a time interval extending outside the model’s domain would have to be clipped before calculating duration. The equation here is supplied as a model, so no additional air-resistance or acceleration assumptions need to be inferred.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b3-flightheight',
      'During a modeled flight, an object’s height is h(t) = 20t − 5t² meters for 0 ≤ t ≤ 4 seconds. For how many seconds in total is the object strictly above a 15-meter platform?',
      2,
      'seconds',
      'The height equals 15 at t = 1 and t = 3 and is greater between those times, giving a two-second interval.',
      ['functions', 'apply'],
      0,
    ),
  ),
  quantitative(
    'math-larger-die',
    'math',
    'Keep the larger roll',
    [
      section('distribution', 'Find the distribution of a maximum', [
        'An expected value is a probability-weighted average of a numerical outcome. It describes the mean across many repetitions of a specified experiment. It need not be a value that occurs in any single repetition. To average the maximum of two dice, first find how often each possible maximum occurs; averaging the two individual dice answers a different question.',
        'For the maximum to be at most k, both dice must be at most k. A fair six-sided die has probability k/6 of meeting that bound for integer k from zero through six. Independence permits multiplying these two probabilities. The event “maximum exactly k” is what remains after subtracting “maximum at most k − 1” from “maximum at most k.”',
        equation(
          'Cumulative count',
          'P(maximum ≤ k)',
          '(k/6)²',
          'Both independent dice must lie among their first k faces.',
        ),
        example(
          'Two three-sided dice',
          'For independent fair dice numbered 1, 2 and 3, there are nine equally likely ordered outcomes. One outcome has maximum 1. Four outcomes have maximum at most 2, so three have maximum exactly 2. The remaining five have maximum 3.',
          table(
            'Maximum distribution',
            ['Maximum', 'Outcome count', 'Weighted contribution'],
            [
              ['1', '1', '1/9'],
              ['2', '3', '6/9'],
              ['3', '5', '15/9'],
            ],
          ),
          'The expected maximum is (1 + 6 + 15)/9 = 22/9. The counts total nine, and the mean lies between the smallest and largest possible outcomes. These are useful checks on a probability table.',
        ),
        'There is another way to average a positive integer outcome. An outcome of size m contributes one unit for each threshold 1 through m that it reaches. Therefore its expectation is the sum of the probabilities of reaching each threshold. This is called a tail sum; it follows from counting those units across outcomes, not from a special rule about dice.',
      ]),
      section(
        'apply',
        'Weight each possible larger roll',
        [
          'For two six-sided dice, the numbers of outcomes with maxima 1 through 6 are 1, 3, 5, 7, 9 and 11. Each is k² − (k − 1)² = 2k − 1. The counts add to 36, the total number of ordered rolls.',
          table(
            'The weighted total',
            ['Maximum', 'Count', 'Maximum × count'],
            [
              ['1', '1', '1'],
              ['2', '3', '6'],
              ['3', '5', '15'],
              ['4', '7', '28'],
              ['5', '9', '45'],
              ['6', '11', '66'],
            ],
          ),
          equation(
            'Expected larger roll',
            'E(maximum)',
            '161/36 ≈ 4.47222',
            'Divide the total weighted value by 36 equally likely rolls.',
          ),
          'The independent tail-sum check gives 6 − (0² + 1² + 2² + 3² + 4² + 5²)/36 = 161/36. The answer exceeds the mean of one die, 3.5, as it should: keeping the larger can never make a particular result smaller than the first die. It remains below six because not every pair includes a six.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b3-maxdie',
      'Roll two independent fair six-sided dice numbered 1 through 6 and keep only the larger value (keep that same value if they tie). What is the expected kept value? Give a fraction or a decimal accurate to four places.',
      161 / 36,
      'expected value',
      'The maximum has counts 1, 3, 5, 7, 9 and 11 among the 36 outcomes; its weighted mean is 161/36.',
      ['distribution', 'apply'],
    ),
  ),
  quantitative(
    'math-cone-water',
    'math',
    'Water in a conical tank',
    [
      section('similarity', 'A changing height also changes the radius', [
        'A cone with its tip at the bottom does not have a constant cross-sectional area. Near the tip the water surface is small; higher up it is wider. Consequently filling half the height is not the same as filling half the volume. A cylinder would have a constant area and a linear height-volume relationship, but a cone does not.',
        'Take a vertical slice through the cone’s axis. The outline is a triangle, and the outline below the water level is a smaller similar triangle. Similar triangles have corresponding lengths in the same ratio. If the full radius is R and full height H, the water-surface radius r at depth h therefore satisfies r/R = h/H.',
        {
          kind: 'diagram',
          drawing: 'cone-similarity',
          caption: 'Similar cones scale volume with the cube of length.',
          description:
            'A cone cross-section contains a smaller filled cone at one quarter of its height. Its radius is also one quarter, so its base area is one sixteenth and volume one sixty-fourth of the full cone.',
        },
        equation(
          'Similar cross-sections',
          'r',
          'R × h/H',
          'Both radius and height scale together from the bottom tip.',
        ),
        'The volume of a cone is one third of the volume of the cylinder with the same base and height: V = πr²h/3. Substituting the radius-height relationship makes volume proportional to h³. The cube has a geometric meaning: two powers come from the base area and one from height. This assumes the tank is a true cone and the water surface is horizontal.',
        'The cubic scaling can also be understood without the one-third formula. Imagine stacking thin horizontal slices. Scaling every length by a factor s scales each slice’s area by s² and its thickness by s. Each slice’s volume therefore scales by s³, and adding all the slices preserves that same factor. This argument applies to any pair of similar three-dimensional shapes, not only cones.',
        example(
          'A smaller similar cone',
          'A cone has full height 8 cm and radius 4 cm. At water depth 2 cm, every linear dimension of the filled cone is one quarter of the full dimension. The volume fraction is therefore (1/4)³ = 1/64, even though the height fraction is 1/4.',
          table(
            'Scale all lengths together',
            ['Length fraction', 'Base-area fraction', 'Volume fraction'],
            [
              ['1', '1', '1'],
              ['1/2', '1/4', '1/8'],
              ['1/4', '1/16', '1/64'],
            ],
          ),
          'The one-third and π factors cancel when comparing similar cone volumes. That cancellation is why the fractional calculation does not require a decimal approximation to π.',
        ),
      ]),
      section(
        'apply',
        'Invert the volume fraction',
        [
          'For the daily tank, a depth h corresponds to fraction (h/12)³ of its full volume. Half full by volume means (h/12)³ = 1/2. Take the positive cube root, because depth is positive, and multiply by 12.',
          equation(
            'Half-volume depth',
            'h',
            '12 × (1/2)^(1/3) ≈ 9.5244 cm',
            'The water reaches about 79.37% of the tank’s height.',
          ),
          'At height 6 cm the tank would contain only one eighth of its full volume, so a half-volume depth must be well above 6 cm. Direct substitution gives r = h/2 and volume πh³/12, which equals half of the full 144π cubic centimeters at the same depth. Radius 6 cm matters for absolute volume but cancels from the fraction. Turning the tank upside down would change the water shape and invalidate this tip-down formula.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b3-cone',
      'An empty right circular conical tank stands with its tip at the bottom and its open circular rim horizontal. Its height is 12 cm and rim radius is 6 cm. Water fills exactly half of the tank’s total volume. What is the water depth measured vertically from the tip? Give centimeters to at least three decimal places.',
      12 * Math.cbrt(0.5),
      'centimeters',
      'Similar triangles give a volume fraction (h/12)³; setting it to one half yields h ≈ 9.5244 cm.',
      ['similarity', 'apply'],
      0.001,
    ),
  ),
];
